import axios from 'axios'

import { parseAuthenticationHeader } from 'helpers/Functions'
import {
  getWithRedirect,
  patchWithRedirect,
  postWithRedirect,
  putWithRedirect
} from 'utils/axios'

import type {
  TAcceptAnalysisTask,
  TGetAnalysisResponse,
  TAnalysisDto,
  TGenerateAnalysisRequestBody,
  TUpdateAnalysisRequestBody,
  TUpdateAnalysisSelectionRequestBody
} from '@homepal/api-contract'

import { AnalysisGeneration } from 'types/AnalysisGeneration'
import { EAnalysisStatus } from 'types/Enums'
import { TCustomizedMetric } from 'types/GlobalMetric'

export type TAnalysisResponse = TAnalysisDto<EAnalysisStatus, TCustomizedMetric>

const BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3990/'
    : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

const URL = `${BASE_URL}v1/analyses`

export async function getAll(): Promise<{
  data: { data: TAnalysisResponse[] }
}> {
  return getWithRedirect<TAnalysisResponse[]>(URL)
}

/** The analysis, with its analysis type beside it under `included`. */
export type TAnalysisDetailResponse = TGetAnalysisResponse<
  EAnalysisStatus,
  TCustomizedMetric
>

export async function getOne(
  id: string
): Promise<{ data: TAnalysisDetailResponse }> {
  // getWithRedirect only types `data`; the body carries `included` too.
  return getWithRedirect<TAnalysisResponse>(`${URL}/${id}`) as Promise<{
    data: TAnalysisDetailResponse
  }>
}

export async function update(
  id: string,
  body: TUpdateAnalysisRequestBody<EAnalysisStatus>
): Promise<{ data: { data: TAnalysisResponse } }> {
  return patchWithRedirect<TAnalysisResponse>(`${URL}/${id}`, { data: body })
}

export async function updateSelection(
  id: string,
  names: TUpdateAnalysisSelectionRequestBody
): Promise<{ data: { data: TAnalysisResponse } }> {
  return putWithRedirect<TAnalysisResponse>(`${URL}/${id}/selection`, {
    data: names
  })
}

export async function destroy(id: string) {
  return axios.delete(`${URL}/${id}`, parseAuthenticationHeader())
}

/** Runs the analysis again in place with its own exclusions: the run. */
export async function generate(
  id: string,
  body: TGenerateAnalysisRequestBody
): Promise<AnalysisGeneration> {
  const response = await postWithRedirect<AnalysisGeneration>(
    `${URL}/${id}/generate`,
    { data: body }
  )

  return response.data.data
}

export async function accept(
  id: string,
  tasks: TAcceptAnalysisTask[]
): Promise<{ data: { data: TAnalysisResponse } }> {
  return postWithRedirect<TAnalysisResponse>(`${URL}/${id}/accept`, {
    data: { tasks }
  })
}
